import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionPlans } from '../contexts/SubscriptionPlansContext';
import { 
  Gamepad2, 
  Trophy, 
  Users, 
  Zap, 
  ArrowRight,
  CheckCircle,
  Star,
  LogIn
} from 'lucide-react';
import BotInvite from '../components/BotInvite';
import LoadingSpinner from '../components/LoadingSpinner';

const Home = () => {
  const { isAuthenticated } = useAuth();
  const { plans, isLoading: plansLoading } = useSubscriptionPlans();

  // Helper function to handle Discord authentication
  const handleDiscordAuth = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/discord/url`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        alert('Failed to get Discord login URL. Please try again.');
      }
    } catch (error) {
      console.error('Error getting Discord auth URL:', error);
      alert('Failed to connect to server. Please make sure the server is running.');
    }
  };

  const features = [
    {
      icon: Gamepad2,
      title: 'Interactive Emoji Games',
      description: 'Play Tic-Tac-Toe, Number Guess, and Rock Paper Scissors with beautiful emoji interfaces'
    },
    {
      icon: Trophy,
      title: 'Leaderboards & Stats',
      description: 'Track your progress, compete with friends, and climb the leaderboards'
    },
    {
      icon: Users,
      title: 'Discord Integration',
      description: 'Seamlessly integrated with Discord servers for multiplayer gaming experiences'
    },
    {
      icon: Zap,
      title: 'Real-time Gaming',
      description: 'Play games in real-time with instant updates and smooth gameplay'
    }
  ];

  const games = [
    {
      name: 'Tic-Tac-Toe',
      emoji: '❌⭕',
      description: 'Classic 3x3 grid game with AI opponent',
      difficulty: ['Easy', 'Medium', 'Hard']
    },
    {
      name: 'Number Guess',
      emoji: '🔢',
      description: 'Guess the number with hints and clues',
      difficulty: ['Easy', 'Medium', 'Hard']
    },
    {
      name: 'Rock Paper Scissors',
      emoji: '🪨📄✂️',
      description: 'Classic hand game with multiple rounds',
      difficulty: ['Easy', 'Medium', 'Hard']
    }
  ];

  // Transform database plans to match UI format
  const transformedPlans = React.useMemo(() => {
    if (!plans || plans.length === 0) return [];
    
    return plans.map((plan, index) => {
      // Build features list from plan features object
      const featuresList = [];
      
      // Add basic features based on limits
      if (plan.limits?.gamesPerDay === 'unlimited' || plan.limits?.gamesPerDay > 10) {
        featuresList.push('Unlimited games');
      } else {
        featuresList.push(`${plan.limits?.gamesPerDay || 10} games per day`);
      }
      
      // Add features from plan.features object
      if (plan.features?.analytics) featuresList.push('Analytics dashboard');
      if (plan.features?.customThemes) featuresList.push('Custom themes');
      if (plan.features?.prioritySupport) featuresList.push('Priority support');
      if (plan.features?.apiAccess) featuresList.push('API access');
      if (plan.features?.whiteLabel) featuresList.push('White-label options');
      if (plan.features?.customBranding) featuresList.push('Custom branding');
      
      // Always include these
      featuresList.push('Discord integration');
      featuresList.push('Real-time gaming');
      
      // Mark second plan as popular (usually Pro)
      const popular = index === 1 && plan.price > 0;
      
      return {
        id: plan.id,
        name: plan.name,
        price: plan.price === 0 ? '$0' : `$${(plan.price / 100).toFixed(2)}`,
        period: plan.interval === 'forever' ? 'forever' : plan.interval,
        features: featuresList,
        popular: popular,
        availableGames: plan.availableGames || []
      };
    });
  }, [plans]);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-discord-blurple via-discord-fuchsia to-discord-blurple">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 text-shadow-lg">
              Discord Game Bot
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
              The ultimate interactive emoji gaming experience for Discord servers. 
              Play, compete, and have fun with your community!
            </p>
            
            {isAuthenticated ? (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/dashboard"
                  className="btn btn-primary bg-white text-discord-blurple hover:bg-white/90 text-lg px-8 py-3"
                >
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/games"
                  className="btn btn-secondary border-white text-white hover:bg-white/10 text-lg px-8 py-3"
                >
                  Play Games
                </Link>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleDiscordAuth}
                    className="btn btn-primary bg-white text-discord-blurple hover:bg-white/90 text-lg px-8 py-3"
                  >
                    Get Started Free
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </button>
                  
                  <Link
                    to="/login"
                    className="btn btn-secondary text-white hover:bg-discord-dark/80 text-lg px-8 py-3 flex items-center justify-center"
                  >
                    <LogIn className="mr-2 h-5 w-5" />
                    Login
                  </Link>
                </div>
                <Link
                  to="/leaderboard"
                  className="btn btn-secondary border-white text-white hover:bg-white/10 text-lg px-8 py-3"
                >
                  View Leaderboard
                </Link>
              </div>
            )}
            
            {/* Bot Invite Section */}
            <div className="mt-8">
              <BotInvite variant="hero" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-discord-darkest">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-discord-lightest mb-4">
              Why Choose Our Discord Bot?
            </h2>
            <p className="text-xl text-discord-light max-w-2xl mx-auto">
              Experience the most engaging and interactive gaming platform designed specifically for Discord communities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="card text-center hover:scale-105 transition-transform duration-300">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 bg-discord-blurple/20 rounded-full">
                      <Icon className="h-8 w-8 text-discord-blurple" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-discord-lightest mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-discord-light">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Games Section */}
      <section className="py-20 bg-discord-darker">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-discord-lightest mb-4">
              Available Games
            </h2>
            <p className="text-xl text-discord-light max-w-2xl mx-auto">
              Play a variety of interactive emoji games with different difficulty levels and AI opponents.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {games.map((game, index) => (
              <div key={index} className="card text-center hover:scale-105 transition-transform duration-300">
                <div className="text-6xl mb-4">{game.emoji}</div>
                <h3 className="text-2xl font-bold text-discord-lightest mb-2">
                  {game.name}
                </h3>
                <p className="text-discord-light mb-4">
                  {game.description}
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {game.difficulty.map((diff, i) => (
                    <span key={i} className="badge badge-primary">
                      {diff}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-discord-darkest">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-discord-lightest mb-4">
              Choose Your Plan
            </h2>
            <p className="text-xl text-discord-light max-w-2xl mx-auto">
              Start free and upgrade as you grow. All plans include Discord integration and real-time gaming.
            </p>
          </div>

          {plansLoading ? (
            <div className="flex justify-center">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
                {transformedPlans.map((plan, index) => (
              <div
                key={plan.id || index}
                className={`card relative ${
                  plan.popular ? 'ring-2 ring-discord-blurple scale-105' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-discord-blurple text-white px-3 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-discord-lightest mb-2">
                    {plan.name}
                  </h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-discord-lightest">
                      {plan.price}
                    </span>
                    <span className="text-discord-light">/{plan.period}</span>
                  </div>
                  
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center text-discord-light">
                        <CheckCircle className="h-5 w-5 text-discord-green mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  {isAuthenticated ? (
                    <Link
                      to="/subscription"
                      className={`btn w-full ${
                        plan.popular ? 'btn-primary' : 'btn-secondary'
                      }`}
                    >
                      {plan.name === 'Free' ? 'Current Plan' : 'Upgrade'}
                    </Link>
                  ) : (
                    <button
                      onClick={handleDiscordAuth}
                      className={`btn w-full ${
                        plan.popular ? 'btn-primary' : 'btn-secondary'
                      }`}
                    >
                      Get Started
                    </button>
                  )}
                </div>
              </div>
            ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-discord-blurple to-discord-fuchsia">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Gaming?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join thousands of Discord communities already using our bot for interactive gaming experiences.
          </p>
          
          {!isAuthenticated && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleDiscordAuth}
                className="btn bg-white text-discord-blurple hover:bg-white/90 text-lg px-8 py-3"
              >
                <Star className="mr-2 h-5 w-5" />
                Start Playing Now
              </button>
              <Link
                to="/leaderboard"
                className="btn border-white text-white hover:bg-white/10 text-lg px-8 py-3"
              >
                View Community
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;
